import React, { useCallback, useEffect, useState } from 'react';
import { DeviceDetail } from '../../api/device';
import {
    SchemaGroup,
    deviceSchemaClient,
    groupAppliesToDevice,
    listDeviceExtValues,
    parseGroupFields,
} from '../../api/schema';
import { alertApprovalIfPending, ApiError } from '../../api/request';
import { alertDialog } from '../../utils/dialog';
import { useCan } from '../../store';
import {
    CAPABILITY_GROUP,
    SchemaFieldInput,
    ExtFieldValue,
    buildExtPatch,
    extValueToInput,
    saveExtPatch,
    schemaFieldText,
    useDictOptions,
    withListTypes,
} from './deviceSchemaForm';

// 设备能力卡片 (设备工况 Profile): 表单定义来自 Schema (device_schema group=capability),
// 值统一读写 device_ext_value (2026-08-14 store=column 机制移除).
// 与 材料理化 / 参数模板 同一模式: Schema 管字段清单/名称/单位, 不管值.

function errMsg(e: unknown): string {
    return e instanceof ApiError ? `${e.code}: ${e.message}` : String(e);
}

function DeviceCapabilityCard({ detail, onChanged }: { detail: DeviceDetail; onChanged: () => void }) {
    const canUpdate = useCan('device', 'update');
    const [group, setGroup] = useState<SchemaGroup | null>(null);
    const [extVals, setExtVals] = useState<Record<string, ExtFieldValue>>({});
    const [edit, setEdit] = useState<Record<string, string> | null>(null);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        // 组定义 + ext 值随设备详情下发 (DeviceDetail.schemaGroups/extValues, 后端已按框架+设备绑定过滤),
        // 工况页三个卡片共用同一份; 详情里没带 (老后端) 才退回自取.
        const [gs, vals] =
            detail.schemaGroups && detail.extValues
                ? [detail.schemaGroups, detail.extValues]
                : await Promise.all([deviceSchemaClient.list(), listDeviceExtValues(detail.deviceKey)]);
        // Schema 显式绑定: 设备绑定其框架模板 (schemaCode = archCode), 只认模板自含的 capability 组;
        // 模板未定义 = 该框架没有能力字段 (如热压不该有激光字段), 不渲染.
        // 组内设备绑定: 组限定了设备清单时, 未绑定本设备的组同样不渲染.
        const g =
            gs.find(
                (x) =>
                    x.isEnabled &&
                    x.groupKey === CAPABILITY_GROUP &&
                    x.archCode === detail.archCode &&
                    groupAppliesToDevice(x, detail.deviceKey),
            ) || null;
        setGroup(g);
        const v = vals.find((x) => x.groupKey === CAPABILITY_GROUP);
        try {
            setExtVals(v ? (JSON.parse(v.params) as Record<string, ExtFieldValue>) : {});
        } catch {
            setExtVals({});
        }
        // updatedAt 入依赖: 能力值经"编辑设备"弹窗保存后, 后端会 bump 设备 updated_at,
        // 父组件重取 detail 时本卡片跟着重拉 ext 值 (只依赖 deviceKey 会一直显示挂载时的旧值).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [detail.deviceKey, detail.archCode, detail.updatedAt, detail.schemaGroups, detail.extValues]);

    useEffect(() => {
        setEdit(null);
        load().catch(() => {});
    }, [load]);

    const fields = withListTypes(parseGroupFields(group?.fields), extVals);
    const dictOpts = useDictOptions(fields.map((f) => f.optionsSource));

    if (!group) {
        return (
            <div className="text-xs text-slate-400 py-2">
                该框架暂无能力字段模板, 可在 Schema管理 → 设备模板 为「{detail.archCode}」新建字段组
            </div>
        );
    }

    const valueOf = (key: string): ExtFieldValue | undefined => extVals[key];

    const startEdit = () => {
        const next: Record<string, string> = {};
        for (const f of fields) {
            const v = valueOf(f.key);
            next[f.key] = extValueToInput(v);
        }
        setEdit(next);
    };

    const save = async () => {
        if (!edit) return;
        const { extPatch, invalid } = buildExtPatch(fields, edit);
        if (invalid) {
            alertDialog(`「${invalid.field.label}」不是有效数字: ${invalid.raw}`);
            return;
        }
        setBusy(true);
        try {
            const message = await saveExtPatch(detail.deviceKey, CAPABILITY_GROUP, extPatch);
            onChanged(); // 父级重取详情 → 新的 schemaGroups/extValues 回流本卡片
            if (!detail.schemaGroups) await load(); // 退回自取模式时才需要自己重拉

            setEdit(null);
            alertApprovalIfPending(message);
        } catch (e) {
            alertDialog(`保存失败: ${errMsg(e)}`);
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            {fields.map((f) => {
                if (edit) {
                    return (
                        <div key={f.key} className="flex items-center justify-between gap-2 py-1">
                            <span className="text-slate-500 text-sm shrink-0">
                                {f.label}
                                {f.unit && <span className="text-slate-300 text-xs"> ({f.unit})</span>}
                            </span>
                            <SchemaFieldInput
                                field={f}
                                value={edit[f.key] ?? ''}
                                onChange={(v) => setEdit({ ...edit, [f.key]: v })}
                                dictOpts={dictOpts}
                                selectClassName="sel w-36 text-sm"
                                inputClassName="inp w-36 text-sm font-mono"
                            />
                        </div>
                    );
                }
                const v = valueOf(f.key);
                return (
                    <div
                        key={f.key}
                        className="flex items-baseline justify-between text-sm py-1.5 border-b border-slate-50 last:border-b-0"
                    >
                        <span className="text-slate-500">{f.label}</span>
                        <span className="text-slate-800 text-right">
                            {v == null || v === '' || (Array.isArray(v) && !v.length) ? (
                                <span className="text-slate-300">—</span>
                            ) : (
                                <span className="font-mono">{schemaFieldText(f, v, dictOpts)}</span>
                            )}
                            {f.unit && <span className="text-slate-400 text-xs ml-1">{f.unit}</span>}
                        </span>
                    </div>
                );
            })}
            <div className="flex justify-end gap-2 mt-2">
                {edit ? (
                    <>
                        <button type="button" className="btn-ghost text-xs" disabled={busy} onClick={() => setEdit(null)}>
                            取消
                        </button>
                        <button type="button" className="btn-primary text-xs" disabled={busy} onClick={save}>
                            {busy ? '保存中…' : '保存'}
                        </button>
                    </>
                ) : (
                    <button type="button" className="text-xs text-brand-600 hover:underline" onClick={startEdit} hidden={!canUpdate}>
                        ✎ 编辑
                    </button>
                )}
            </div>
        </>
    );
}

export default DeviceCapabilityCard;
